"use client";

// ===========================================
// konakla - Arama Sonuçları Haritası (çoklu marker + fiyat pin'leri)
// ===========================================
// Sonuç sayfasında (split görünüm sağı + mobil tam-ekran) kullanılır.
// location-map.tsx ile aynı Leaflet/CARTO altyapısını kullanır ama tek marker
// yerine her ilan için fiyat pin'i + popup mini-kart gösterir.

import { useEffect, useMemo, useState } from "react";
import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { Loader2, MapPin } from "lucide-react";
import type { DivIcon } from "leaflet";

import "leaflet/dist/leaflet.css";

const MapContainer = dynamic(() => import("react-leaflet").then((m) => m.MapContainer), {
  ssr: false,
});
const TileLayer = dynamic(() => import("react-leaflet").then((m) => m.TileLayer), { ssr: false });
const Marker = dynamic(() => import("react-leaflet").then((m) => m.Marker), { ssr: false });
const Popup = dynamic(() => import("react-leaflet").then((m) => m.Popup), { ssr: false });
const FitBounds = dynamic(
  () => import("@/components/common/map-helpers").then((m) => m.FitBounds),
  {
    ssr: false,
  }
);

export interface MapListing {
  id: string;
  slug: string;
  title: string;
  lat: number;
  lng: number;
  price: number;
  currency: "TRY" | "USD" | "EUR";
  coverImage: string;
  city: string;
  district: string;
}

const CURRENCY_SYMBOL: Record<string, string> = { TRY: "₺", USD: "$", EUR: "€" };

function formatPrice(price: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? "";
  return `${sym}${Math.round(price).toLocaleString("tr-TR")}`;
}

type SpreadListing = MapListing & { dLat: number; dLng: number };

// Aynı (~1m) koordinattaki ilanları (ör. ev sahibi "varsayılan konum" seçmiş →
// pin'ler üst üste biner) küçük bir spiral üzerine dağıt → HEPSİ görünür ve
// tıklanabilir olur. Popup gerçek ilanı gösterir; yalnız pin konumu ~ birkaç on
// metre kaydırılır (varsayılan/yaklaşık konumlar için sorun değil).
function spreadOverlapping(listings: MapListing[]): SpreadListing[] {
  const groups = new Map<string, MapListing[]>();
  for (const l of listings) {
    const key = `${l.lat.toFixed(5)},${l.lng.toFixed(5)}`;
    const g = groups.get(key);
    if (g) g.push(l);
    else groups.set(key, [l]);
  }

  const GOLDEN = 2.399963; // altın açı → düzgün spiral dağılım
  const BASE = 0.00035; // ~38m taban yarıçap
  const out: SpreadListing[] = [];
  for (const group of groups.values()) {
    if (group.length === 1) {
      out.push({ ...group[0], dLat: group[0].lat, dLng: group[0].lng });
      continue;
    }
    group.forEach((l, i) => {
      const radius = BASE * Math.sqrt(i + 1);
      const angle = i * GOLDEN;
      out.push({
        ...l,
        dLat: l.lat + radius * Math.cos(angle),
        dLng: l.lng + radius * Math.sin(angle),
      });
    });
  }
  return out;
}

function MapCard({ listing }: { listing: MapListing }) {
  return (
    <Link href={`/listings/${listing.slug}`} className="block w-52">
      {/* Kapak boş olabilir → koşullu (next/image boş src'de patlar). */}
      {listing.coverImage && (
        <div className="relative h-28 w-full overflow-hidden rounded-md">
          <Image
            src={listing.coverImage}
            alt={listing.title}
            fill
            sizes="220px"
            className="object-cover"
          />
        </div>
      )}
      <div className="mt-2">
        <p className="text-foreground line-clamp-1 text-sm font-semibold">{listing.title}</p>
        <p className="text-muted-foreground text-xs">
          {listing.district}, {listing.city}
        </p>
        <p className="text-foreground mt-1 text-sm font-bold">
          {formatPrice(listing.price, listing.currency)}{" "}
          <span className="text-muted-foreground text-xs font-normal">/ gece</span>
        </p>
      </div>
    </Link>
  );
}

interface SearchResultsMapProps {
  listings: MapListing[];
  className?: string;
}

export function SearchResultsMap({ listings, className = "h-full w-full" }: SearchResultsMapProps) {
  const [isClient, setIsClient] = useState(false);
  const [makeIcon, setMakeIcon] = useState<((label: string) => DivIcon) | null>(null);

  useEffect(() => {
    setIsClient(true);
    import("leaflet").then((L) => {
      // Fiyat pin'i — dinamik genişlikli pill. iconSize [0,0] + CSS translate(-50%,-50%)
      // ile pin koordinatın tam üstünde ortalanır (genişlikten bağımsız).
      setMakeIcon(
        () => (label: string) =>
          L.divIcon({
            className: "konakla-pin-wrapper",
            html: `<div class="konakla-pin">${label}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          })
      );
    });
  }, []);

  // Üst üste binen (aynı koordinatlı) ilanları spiral'e dağıt → hepsi görünür.
  const spread = useMemo(() => spreadOverlapping(listings), [listings]);
  const points = useMemo(() => spread.map((l) => [l.dLat, l.dLng] as [number, number]), [spread]);
  const boundsKey = useMemo(() => points.map((p) => `${p[0]},${p[1]}`).join(";"), [points]);

  if (!isClient || !makeIcon) {
    return (
      <div className={`${className} bg-muted flex items-center justify-center`}>
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (listings.length === 0) {
    return (
      <div
        className={`${className} bg-muted flex flex-col items-center justify-center gap-2 p-6 text-center`}
      >
        <MapPin className="text-muted-foreground h-8 w-8" />
        <p className="text-muted-foreground text-sm">Bu sonuçlar için konum bilgisi bulunmuyor.</p>
      </div>
    );
  }

  return (
    <div className={className}>
      <MapContainer
        center={[listings[0].lat, listings[0].lng]}
        zoom={12}
        scrollWheelZoom
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
        />
        <FitBounds points={points} boundsKey={boundsKey} />
        {spread.map((l) => (
          <Marker
            key={l.id}
            position={[l.dLat, l.dLng]}
            icon={makeIcon(formatPrice(l.price, l.currency))}
          >
            <Popup>
              <MapCard listing={l} />
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
